import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { Search } from "@/components/search/Search.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { EmployeeViewModal } from "@/pages/employees/widgets/EmployeeViewModal.tsx";
import {
  TimeOffCalendarCell,
  TimeOffCalendarEventInfo,
} from "@/pages/time-off/components/TimeOffCalendarCell.tsx";
import { TimeOffCalendarPeriod } from "@/pages/time-off/components/TimeOffCalendarPeriod.tsx";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useTimeOffCalendar } from "@/queries/use-time-off.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { ApiCalendarHolidayDateType } from "@/types/calendar-holiday.types.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { ApiPermission, EmployeeFieldPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import {
  ApiTimeOffCalendarFilter,
  ApiTimeOffLogFullPreview,
  getTimeOffCalendarPeriods,
  TimeOffCalendarPeriodModel,
  TimeOffCalendarPeriodType,
} from "@/types/time-off/time-off.types.ts";
import { DatesSetArg } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import timelinePlugin from "@fullcalendar/timeline";
import {
  Box,
  Button,
  ButtonLink,
  CoreFilterType,
  FontAwesomeIcon,
  Page,
  parseUrlHelper,
  Skeleton,
  Stack,
  Text,
  Title,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useDebouncedSearchParam,
  useModals,
} from "@skailer-com/uikit";
import { addMonths, endOfMonth, startOfMonth } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffectOnce } from "react-use";

export interface TimeOffRequestExtendedProps {
  request?: ApiTimeOffLogFullPreview;
  holidayTitle?: string;
  holidayType?: ApiCalendarHolidayDateType;
}

export interface TimeOffRowExtendedProps {
  employee: ApiEmployee | undefined;
  title?: string;
  isLoading?: boolean;
}

interface TimeOffCalendarEvent {
  id: string;
  resourceId: string;
  title: string;
  start: Date;
  end: Date;
  display: string;
  className: string;
  extendedProps: TimeOffRequestExtendedProps;
}

interface TimeOffCalendarRow {
  id: string;
  index: number;
  extendedProps: TimeOffRowExtendedProps;
}

export const TimeOffCalendar = () => {
  const { isFeatureEnabled, canIEdit, canEditField } = usePermissions();
  const { renderEmployeeName } = useFormatter();
  const { openModal } = useModals();
  const { t } = useTranslation();
  const { data: employeeMe } = useEmployeeMe();
  const navigate = useNavigate();
  useTitle(t("time_off:time_off_calendar"));
  const rowsRef = useRef<TimeOffCalendarRow[]>([]);

  const [, setSearchParams] = useSearchParams();

  const [hoveredHoliday, setHoveredHoliday] = useState<{
    id: string;
    title: string;
    x: number;
    y: number;
  } | null>(null);
  const [period, setPeriod] = useState<TimeOffCalendarPeriodModel>(
    getTimeOffCalendarPeriods().find(
      (period) => period.type == TimeOffCalendarPeriodType.threeMonth
    )!
  );
  const calendarRef = useRef<FullCalendar>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { canCurrentEmployeeViewField } = usePermissions();
  const canFilterInCalendar = canCurrentEmployeeViewField(
    EmployeeFieldPermission.employee_position_department
  );

  const {
    value: searchValue,
    setValue: setSearchValue,
    clear: clearSearch,
  } = useDebouncedSearchParam({
    paramName: "search",
    delay: 300,
  });

  const library = filtersLibrary();
  const { filter, filterButton, isFilterReady } = useAppFilter<ApiTimeOffCalendarFilter>({
    filters: [
      {
        ...library.page_number,
        defaultValue: 1,
      },
      library.text,
      library.category_ids,
      {
        ...library.employee_ids,
        name: "manager_ids",
        label: t("time_off:filter_employee_subordinates"),
      },
      library.job_profile_ids,
      library.job_role_ids,
      library.department_ids,
      library.team_ids,
      library.division_ids,
      library.location_ids,
      {
        ...library.employee_lifecycle_statuses,
        // defaultValue: getActiveEmployeeLifecycleStatuses(),
      },
      library.employment_type_ids,
      library.employment_contract_ids,
      {
        name: "employee_id",
        type: CoreFilterType.hidden,
        valueType: library.page_number.valueType,
        renderType: library.page_number.renderType,
        hideInFilter: true,
        defaultValue: employeeMe?.id ?? 0,
      },
      {
        ...library.date_from,
        hideInFilter: true,
      },
      {
        ...library.date_to,
        hideInFilter: true,
      },
    ],
    source: "Time Off Calendar",
  });

  const { data, isFetching } = useTimeOffCalendar(
    filter.filter,
    !!filter.filter.date_from && isFilterReady
  );

  const requests = useMemo<TimeOffCalendarEvent[]>(() => {
    const nextDate = (date: Date): Date => {
      const newDate = new Date(date);
      newDate.setDate(newDate.getDate() + 1);
      return newDate;
    };

    const newRequests =
      data?.flatMap((record) =>
        record.requests.map((request) => ({
          id: `${request.id}`,
          resourceId: `${record.employee.id}`,
          title: `${request.time_off_data.category.title}`,
          start: request.time_off_data.date_from,
          end: nextDate(request.time_off_data.date_to),
          className: "",
          display: "",
          extendedProps: {
            request,
          },
        }))
      ) ?? [];

    const holidays =
      data?.flatMap((record) =>
        record.calendar_holidays.map((holiday) => ({
          id: `holiday_${holiday.id}_${record.employee.id}`,
          resourceId: `${record.employee.id}`,
          title: `${holiday.title}`,
          start: holiday.date,
          end: holiday.date,
          className: holiday.type === ApiCalendarHolidayDateType.working ? "working-holiday" : "",
          display: "background",
          extendedProps: {
            holidayTitle: holiday.title,
            holidayType: holiday.type,
          },
        }))
      ) ?? [];

    return [...newRequests, ...holidays];
  }, [data]);

  const rows = useMemo<TimeOffCalendarRow[]>(() => {
    if (isFetching && !data) {
      return Array.from({ length: 8 }).map((_, index) => ({
        id: `loading_${index}`,
        index,
        extendedProps: {
          employee: undefined,
          isLoading: true,
        },
      }));
    }

    let index = 0;
    const seenDepartments = new Set<number>();

    return (
      data?.reduce((acc: TimeOffCalendarRow[], record) => {
        if (
          record.employee.employee_position?.department?.id &&
          !seenDepartments.has(record.employee.employee_position.department.id) &&
          record.employee.id !== employeeMe?.id
        ) {
          acc.push({
            id: `dep_${record.employee.employee_position.department.id}`,
            index: index++,
            extendedProps: {
              employee: undefined,
              title: record.employee.employee_position.department.title,
            },
          });

          seenDepartments.add(record.employee.employee_position.department.id);
        }

        acc.push({
          id: `${record.employee.id}`,
          index: index++,
          extendedProps: {
            employee: record.employee,
          },
        });

        return acc;
      }, []) ?? []
    );
  }, [data, employeeMe?.id, isFetching]);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.timeOffs.calendarViewed);

    const today = new Date();
    const firstDayOfMonth = startOfMonth(today);
    const threeMonthsLater = endOfMonth(addMonths(today, 2));

    setSearchParams((searchParams) => {
      searchParams.set("date_from", parseUrlHelper.formatDate(firstDayOfMonth));
      searchParams.set("date_to", parseUrlHelper.formatDate(threeMonthsLater));

      if (employeeMe?.id) {
        searchParams.set("employee_id", employeeMe.id.toString());
      }

      return searchParams;
    });
  });

  const handleDatesSet = (arg: DatesSetArg) => {
    setSearchParams((searchParams) => {
      searchParams.set("date_from", parseUrlHelper.formatDate(arg.start));
      searchParams.set("date_to", parseUrlHelper.formatDate(arg.end));

      if (employeeMe?.id) {
        searchParams.set("employee_id", employeeMe.id.toString());
      }
      return searchParams;
    });
  };

  const handlePrevClick = () => {
    calendarRef.current?.getApi()?.prev();
  };

  const handleNextClick = () => {
    calendarRef.current?.getApi()?.next();
  };

  const handleTodayClick = () => {
    calendarRef.current?.getApi()?.today();
  };

  const handleChangePeriod = (period: TimeOffCalendarPeriodModel) => {
    const calendarApi = calendarRef.current?.getApi();

    setPeriod(period);
    calendarApi?.changeView(period.view);

    const nowLine = document.querySelector(".fc-timeline-now-indicator-line");
    const scrollView = document
      .querySelectorAll(".fc-scroller-harness-liquid > .fc-scroller")
      .item(1);

    if (scrollView && nowLine) {
      const scrollTo = nowLine.getBoundingClientRect().left;
      scrollView.scrollLeft = scrollTo < 0 ? 0 : scrollTo;
    }
  };

  const openProfile = (employee: ApiEmployee) => {
    openModal(EmployeeViewModal, { employee });
  };

  const canAddTimeOffRequest = (employee?: ApiEmployee) => {
    return (
      isFeatureEnabled(ApiFeatureType.time_off) &&
      (canIEdit(ApiPermission.approve_and_reject_leaves) ||
        canEditField(employee, EmployeeFieldPermission.employee_time_off))
    );
  };

  return (
    <Page className={"h-full overflow-y-auto"}>
      <Page.Header
        title={t("time_off:time_off_calendar")}
        titleSlot={
          <Stack gap={"xs"} direction={"horizontal"} className={"ml-2"}>
            <Button
              variant={"secondary"}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-chevron-left" />}
              onClick={handlePrevClick}
            />
            <Button variant={"secondary"} onClick={handleTodayClick}>
              {t("common:today")}
            </Button>
            <Button
              variant={"secondary"}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-chevron-right" />}
              onClick={handleNextClick}
            />
          </Stack>
        }
      >
        <Stack gap={"md"} direction={"horizontal"}>
          <TimeOffCalendarPeriod chosenPeriod={period.type} onChange={handleChangePeriod} />
          {canFilterInCalendar && filterButton()}

          <ButtonLink
            href={`/time-off/request/new`}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
          >
            {t("time_off:request_timeoff")}
          </ButtonLink>
        </Stack>
      </Page.Header>
      <Page.Content className={"h-full overflow-y-hidden flex flex-col"}>
        <Stack direction={"horizontal"} className={"sticky top-0 bg-light"}>
          <Search
            onInput={(e) => setSearchValue(e.currentTarget.value)}
            type={"text"}
            value={searchValue}
            placeholder={t("common:search")}
            className={"w-96"}
            clearable
            onClear={clearSearch}
          />
        </Stack>
        <Box
          ref={containerRef}
          className={"calendar-timeline w-full grow overflow-y-auto relative"}
        >
          {hoveredHoliday && (
            <Tooltip open={true} key={hoveredHoliday.id}>
              <TooltipTrigger
                asChild
                style={{
                  position: "fixed",
                  left: hoveredHoliday.x,
                  top: hoveredHoliday.y,
                  pointerEvents: "none",
                }}
              >
                <span />
              </TooltipTrigger>
              <TooltipContent>{hoveredHoliday.title}</TooltipContent>
            </Tooltip>
          )}
          <FullCalendar
            ref={calendarRef}
            plugins={[timelinePlugin, dayGridPlugin, interactionPlugin, resourceTimelinePlugin]}
            initialView={period.view}
            views={{
              resourceTimeline3Months: {
                type: "resourceTimeline",
                duration: { months: 3 },
                slotLabelInterval: { days: 1 },
                slotLabelFormat: [{ month: "long", year: "numeric" }, { day: "numeric" }],
              },
              resourceTimelineMonth: {
                type: "resourceTimeline",
                duration: { months: 1 },
                slotLabelInterval: { days: 1 },
                slotLabelFormat: [{ month: "long", year: "numeric" }, { day: "numeric" }],
              },
              resourceTimelineYear: {
                type: "resourceTimeline",
                duration: { years: 1 },
                slotLabelInterval: { days: 1 },
                slotLabelFormat: [{ month: "long", year: "numeric" }, { day: "numeric" }],
              },
            }}
            datesSet={handleDatesSet}
            headerToolbar={false}
            nowIndicator={true}
            resourceAreaWidth={"20%"}
            slotDuration="1 day"
            slotLabelInterval="1 day"
            slotMinWidth={36}
            eventMinHeight={56}
            resourceOrder={"index"}
            resources={rows ?? []}
            events={requests ?? []}
            editable={false}
            height={"100%"}
            resourceAreaHeaderContent={t("core:name")}
            resourceLabelContent={(resourceInfo) => {
              const { extendedProps } = resourceInfo.resource;

              if (extendedProps.isLoading) {
                return (
                  <Stack
                    direction={"horizontal"}
                    gap={"sm"}
                    items={"center"}
                    className="py-2.5 pr-2"
                  >
                    <Skeleton className={"h-9 w-9 rounded-xs shrink-0"} />
                    <Skeleton className={"h-4 w-32"} />
                  </Stack>
                );
              }

              return (
                <Stack className="py-2.5 pr-2">
                  {extendedProps.employee ? (
                    <Stack
                      direction={"horizontal"}
                      items="center"
                      gap={"sm"}
                      className={"cursor-pointer"}
                      onClick={() => openProfile(extendedProps.employee)}
                    >
                      <EmployeeAvatar
                        employee={extendedProps.employee}
                        size={"md"}
                        className="shrink-0"
                      />
                      <Text
                        className={
                          "text-ellipsis overflow-hidden whitespace-nowrap font-semibold mr-1"
                        }
                      >
                        {renderEmployeeName(extendedProps.employee)}
                      </Text>
                    </Stack>
                  ) : (
                    <Stack className={"h-8 truncate"} direction={"horizontal"} items={"center"}>
                      <Title
                        size={"xs"}
                        className={"truncate text-secondary"}
                        header={extendedProps.title}
                      />
                    </Stack>
                  )}
                </Stack>
              );
            }}
            eventContent={(eventInfo: TimeOffCalendarEventInfo) => {
              return (
                <>
                  {eventInfo.event.display != "background" && (
                    <TimeOffCalendarCell eventInfo={eventInfo} />
                  )}
                </>
              );
            }}
            eventMouseEnter={(info) => {
              if (info.event.display === "background") {
                const holidayTitle = info.event.extendedProps.holidayTitle;
                if (holidayTitle) {
                  const rect = info.el.getBoundingClientRect();

                  setHoveredHoliday({
                    id: info.event.id,
                    title: holidayTitle,
                    x: rect.left + rect.width / 2,
                    y: rect.top,
                  });
                }
              }
            }}
            eventMouseLeave={(info) => {
              if (info.event.display === "background") {
                setHoveredHoliday(null);
              }
            }}
            dateClick={(info) => {
              if (!info.resource?.extendedProps.employee) return;

              if (!canAddTimeOffRequest(info.resource?.extendedProps.employee)) return;

              const dateStr = info.dateStr.split("T")[0];

              navigate(
                `/time-off/request/new?startDate=${dateStr}&employeeId=${info.resource?.extendedProps.employee?.id}`
              );
            }}
            slotLaneDidMount={(arg) => {
              const el = arg.el;
              const hoverOverlay = document.createElement("div");

              hoverOverlay.className = "slot-hover-overlay";

              el.appendChild(hoverOverlay);

              const handleMouseMove = (e: MouseEvent) => {
                if (!rowsRef.current.length) return;

                const rect = el.getBoundingClientRect();
                let lineInfo: TimeOffCalendarRow | null = null;
                const parent = el.parentElement;
                const parentRect = parent?.getBoundingClientRect();
                const x = rect.left - (parentRect?.left ?? 0);
                let y = rect.top - (parentRect?.top ?? 0);
                const lines = document.querySelectorAll(
                  ".fc-timeline-body > table.fc-scrollgrid-sync-table tr"
                );

                lines.forEach((line, index) => {
                  const rectLine = line.getBoundingClientRect();

                  if (rectLine.top < e.pageY && rectLine.bottom > e.pageY) {
                    y = rectLine.top - (parentRect?.top ?? 0);
                    lineInfo = rowsRef.current[index];
                  }
                });

                const employee = (lineInfo as TimeOffCalendarRow | null)?.extendedProps?.employee;

                if (!employee || !canAddTimeOffRequest(employee)) {
                  hoverOverlay.style.display = "none";
                  return;
                }

                const slotEl = document.elementFromPoint(e.clientX, e.clientY);
                const slotDate = slotEl?.closest("[data-date]")?.getAttribute("data-date");

                const hasWorkingHoliday = requests.some((event) => {
                  if (
                    event.resourceId !== employee.id.toString() ||
                    event.extendedProps.holidayType !== ApiCalendarHolidayDateType.working
                  ) {
                    return false;
                  }
                  const eventDate = event.start
                    ? event.start.toISOString().slice(0, 10)
                    : String(event.start).slice(0, 10);
                  return eventDate === slotDate;
                });

                const isWeekend =
                  (slotEl?.closest(".fc-day-sat") || slotEl?.closest(".fc-day-sun")) &&
                  !hasWorkingHoliday;
                hoverOverlay.className = `slot-hover-overlay${isWeekend ? " slot-hover-overlay--weekend" : ""}`;

                hoverOverlay.style.left = `${x}px`;
                hoverOverlay.style.top = `${y}px`;
                hoverOverlay.style.width = `${rect.width}px`;
                hoverOverlay.style.display = "block";
              };

              const handleMouseLeave = () => {
                hoverOverlay.style.display = "none";
              };

              el.addEventListener("mousemove", handleMouseMove);
              el.addEventListener("mouseleave", handleMouseLeave);
            }}
          />
        </Box>
      </Page.Content>
    </Page>
  );
};
